// Agrupamento de faturamento (#5778): grupo de UCs de um cliente que fecha e cobra junto.
// Regra pura — sem Supabase — para a tela de gestão (#5781) e a carga do de-para (#5782).
// O banco repete as mesmas travas (CHECK + trigger em agrupamentos_faturamento); aqui é para a
// tela avisar antes de gravar, com mensagem legível em vez do erro do Postgres.

import { validarCNPJ } from './utils.ts';
import type { AgrupamentoFaturamentoInput, VincularUcsAgrupamentoResultado } from '../../types';

export const DIA_VENCIMENTO_PADRAO = 15;

/** Tira máscara e espaços. Não completa zeros: CNPJ digitado com 13 dígitos é erro, não palpite. */
export function normalizarCnpjPagador(valor: string | null | undefined): string {
  return String(valor ?? '').replace(/\D/g, '');
}

/** Código da concessionária é texto: "0044000" mantém os zeros. Vazio/só espaço vira null. */
export function normalizarCodigoAgrupamento(valor: string | null | undefined): string | null {
  const limpo = String(valor ?? '').trim();
  return limpo === '' ? null : limpo;
}

/**
 * Lista de códigos da concessionária digitada num campo só ("0044000, 0066944" ou um por linha).
 * Mantém os zeros à esquerda, tira espaço e repetição, e ordena para comparar conjuntos.
 */
export function normalizarListaCodigos(valor: string | string[] | null | undefined): string[] {
  const partes = Array.isArray(valor) ? valor : String(valor ?? '').split(/[,;\n]+/);
  const unicos = new Set<string>();
  for (const p of partes) {
    const c = normalizarCodigoAgrupamento(p);
    if (c) unicos.add(c);
  }
  return [...unicos].sort();
}

/** Número de UC para comparação (mesmo critério de numero_uc_norm): só dígitos, sem zeros à esquerda. */
export function normalizarNumeroUc(valor: string | number | null | undefined): string {
  return String(valor ?? '').replace(/\D/g, '').replace(/^0+/, '');
}

export function validarAgrupamento(input: AgrupamentoFaturamentoInput): string[] {
  const erros: string[] = [];
  if (!String(input.nome ?? '').trim()) erros.push('Informe o nome do agrupamento.');

  const cnpj = normalizarCnpjPagador(input.cnpj_pagador);
  if (!cnpj) erros.push('Informe o CNPJ do pagador.');
  else if (cnpj.length !== 14 || !validarCNPJ(cnpj)) erros.push('CNPJ do pagador inválido.');

  const dia = input.dia_vencimento ?? DIA_VENCIMENTO_PADRAO;
  if (!Number.isInteger(dia) || dia < 1 || dia > 28) {
    erros.push('Dia de vencimento deve ser um número inteiro entre 1 e 28.');
  }

  // Régua própria (#5787): cada campo é opcional, mas a ordem do ciclo precisa fazer sentido.
  const limite = input.dia_limite_emissao;
  if (limite != null && (!Number.isInteger(limite) || limite < 1 || limite > 31)) {
    erros.push('Dia limite de emissão deve ser um número inteiro entre 1 e 31.');
  }
  const auditoria = input.dia_auditoria_util;
  if (auditoria != null && (!Number.isInteger(auditoria) || auditoria < 1 || auditoria > 20)) {
    erros.push('Auditoria deve cair entre o 1º e o 20º dia útil.');
  }
  const malote = input.dia_malote_util;
  if (malote != null && (!Number.isInteger(malote) || malote < 1 || malote > 20)) {
    erros.push('Malote deve cair entre o 1º e o 20º dia útil.');
  }
  if (auditoria != null && malote != null && malote < auditoria) {
    erros.push('O malote não pode ser entregue antes da auditoria.');
  }
  const edital = input.dias_pagamento_edital;
  if (edital != null && (!Number.isInteger(edital) || edital < 1 || edital > 180)) {
    erros.push('Dias para pagamento devem ser um número inteiro entre 1 e 180.');
  }

  if (input.pagador_uf != null && input.pagador_uf.trim() !== '' && !/^[A-Za-z]{2}$/.test(input.pagador_uf.trim())) {
    erros.push('UF do pagador deve ter 2 letras.');
  }
  if (input.pagador_cep != null && input.pagador_cep.trim() !== '' && input.pagador_cep.replace(/\D/g, '').length !== 8) {
    erros.push('CEP do pagador deve ter 8 dígitos.');
  }
  return erros;
}

export interface LinhaDePara {
  numero_uc: string;
  cod_agrupamento: string;
}

export interface ConferenciaDePara {
  /** Contagem de UCs por código de agrupamento (só linhas válidas). */
  porAgrupamento: Record<string, number>;
  /** UC que aparece em mais de um agrupamento — bloqueia a carga (o banco só aceita 1). */
  ucEmMaisDeUmAgrupamento: { numero_uc: string; codigos: string[] }[];
  /** UC repetida no mesmo agrupamento — inofensiva (a RPC é idempotente), mas sinal de planilha suja. */
  ucRepetidaMesmoAgrupamento: string[];
  /** Linha sem UC ou sem código. */
  linhasIncompletas: number[];
  /** UC da planilha que não existe no cadastro. */
  ucForaDoCadastro: string[];
  /** UC do cadastro esperado (ex.: todas da Prefeitura) que ficou sem agrupamento. */
  ucSemAgrupamento: string[];
  ok: boolean;
}

/**
 * Confere um de-para UC → agrupamento antes da carga.
 * `cadastro` = números de UC que existem; `esperadas` = UCs que precisam terminar agrupadas.
 * Números são comparados normalizados (máscara e zeros à esquerda não importam).
 */
export function conferirDePara(
  linhas: LinhaDePara[],
  cadastro: string[],
  esperadas: string[] = [],
): ConferenciaDePara {
  const porUc = new Map<string, Set<string>>();
  const repetidas = new Set<string>();
  const incompletas: number[] = [];

  linhas.forEach((l, i) => {
    const uc = normalizarNumeroUc(l.numero_uc);
    const cod = normalizarCodigoAgrupamento(l.cod_agrupamento);
    if (!uc || !cod) {
      incompletas.push(i);
      return;
    }
    const cods = porUc.get(uc) ?? new Set<string>();
    if (cods.has(cod)) repetidas.add(uc);
    cods.add(cod);
    porUc.set(uc, cods);
  });

  const porAgrupamento: Record<string, number> = {};
  const multiplas: { numero_uc: string; codigos: string[] }[] = [];
  for (const [uc, cods] of porUc) {
    if (cods.size > 1) multiplas.push({ numero_uc: uc, codigos: [...cods].sort() });
    for (const c of cods) porAgrupamento[c] = (porAgrupamento[c] ?? 0) + 1;
  }

  const noCadastro = new Set(cadastro.map(normalizarNumeroUc).filter(Boolean));
  const fora = [...porUc.keys()].filter(uc => !noCadastro.has(uc));
  const semGrupo = [...new Set(esperadas.map(normalizarNumeroUc).filter(Boolean))].filter(uc => !porUc.has(uc));

  return {
    porAgrupamento,
    ucEmMaisDeUmAgrupamento: multiplas,
    ucRepetidaMesmoAgrupamento: [...repetidas],
    linhasIncompletas: incompletas,
    ucForaDoCadastro: fora,
    ucSemAgrupamento: semGrupo,
    ok: multiplas.length === 0 && incompletas.length === 0 && fora.length === 0 && semGrupo.length === 0,
  };
}

// ─── Pendência: UC que deveria estar num agrupamento e não está ──────────────────────────────

export interface UcParaPendencia {
  id: number;
  numero_uc: string;
  nome_cliente?: string | null;
  cpf_cnpj_cliente?: string | null;
  cpf_cnpj_titular?: string | null;
  empresa_parceira_id?: number | string | null;
  /** `null` = UC fora do rateio: não gera cobrança da Vigor, então não é pendência (Prefeitura de
   *  Morrinhos, 17/09/2026: só as UCs do rateio entram na lista). Ausente = não informado. */
  rateio_unidade_geradora_id?: string | null;
}

/**
 * CNPJs que "puxam" uma UC para um agrupamento: o do pagador de cada grupo ATIVO mais o de cada
 * UC já vinculada a um grupo ativo. A segunda parte é o caso do Fundo Municipal de Saúde, cuja
 * fatura sai num CNPJ diferente do pagador do grupo.
 */
export function cnpjsDeAgrupamento(
  agrupamentosAtivos: { cnpj_pagador: string }[],
  ucsVinculadas: UcParaPendencia[],
): Set<string> {
  const set = new Set<string>();
  for (const a of agrupamentosAtivos) {
    const c = normalizarCnpjPagador(a.cnpj_pagador);
    if (c) set.add(c);
  }
  for (const u of ucsVinculadas) {
    for (const v of [u.cpf_cnpj_cliente, u.cpf_cnpj_titular]) {
      const c = normalizarCnpjPagador(v);
      if (c) set.add(c);
    }
  }
  return set;
}

/**
 * UCs pendentes: sem vínculo, de empresa nenhuma (UC de parceira não entra em agrupamento da
 * Vigor) e com CNPJ de cliente ou de titular na lista acima.
 */
export function ucsPendentesDeAgrupamento(
  ucs: UcParaPendencia[],
  idsVinculadas: Iterable<number>,
  cnpjs: Set<string>,
): UcParaPendencia[] {
  const vinculadas = new Set(idsVinculadas);
  return ucs.filter(u => {
    if (vinculadas.has(u.id)) return false;
    if (u.empresa_parceira_id != null) return false;
    if (u.rateio_unidade_geradora_id === null) return false;
    return [u.cpf_cnpj_cliente, u.cpf_cnpj_titular]
      .map(normalizarCnpjPagador)
      .some(c => !!c && cnpjs.has(c));
  });
}

// ─── Importação de planilha (de-para em massa) ───────────────────────────────────────────────

export interface LinhaImportacao {
  /** Índice da linha na planilha, para a mensagem de erro apontar onde está o problema. */
  linha: number;
  numero_uc: string;
  cod_agrupamento: string;
}

export interface ItemImportacao {
  linha: number;
  numero_uc: string;
  cod_agrupamento: string;
  unidade_consumidora_id: number;
  agrupamento_id: number;
  /** 'vincular' = está sem grupo; 'mover' = está em outro grupo; 'sem_mudanca' = já está neste. */
  acao: 'vincular' | 'mover' | 'sem_mudanca';
  agrupamento_atual_id?: number;
}

export interface ProblemaImportacao {
  linha: number;
  numero_uc: string;
  motivo:
    | 'linha_incompleta'
    | 'uc_fora_do_cadastro'
    | 'agrupamento_desconhecido'
    | 'agrupamento_inativo'
    | 'uc_de_empresa_parceira'
    | 'uc_repetida_em_dois_agrupamentos'
    | 'uc_repetida_na_planilha';
}

export const MOTIVO_IMPORTACAO: Record<ProblemaImportacao['motivo'], string> = {
  linha_incompleta: 'Linha sem número de UC ou sem código de agrupamento',
  uc_fora_do_cadastro: 'UC não existe no cadastro',
  agrupamento_desconhecido: 'Código de agrupamento não cadastrado',
  agrupamento_inativo: 'Agrupamento está inativo',
  uc_de_empresa_parceira: 'UC é de empresa parceira e não entra em agrupamento da Vigor',
  uc_repetida_em_dois_agrupamentos: 'A mesma UC aparece em dois agrupamentos diferentes',
  uc_repetida_na_planilha: 'UC repetida na planilha (a primeira linha vale)',
};

/** Problema que impede aplicar a planilha inteira. Repetição na mesma linha é só aviso. */
export const BLOQUEIA_IMPORTACAO: ProblemaImportacao['motivo'][] = [
  'linha_incompleta', 'uc_fora_do_cadastro', 'agrupamento_desconhecido',
  'agrupamento_inativo', 'uc_de_empresa_parceira', 'uc_repetida_em_dois_agrupamentos',
];

export interface PlanoImportacao {
  itens: ItemImportacao[];
  problemas: ProblemaImportacao[];
  /** Quantas UCs entram em cada agrupamento, pelo código, contando só o que muda algo. */
  porAgrupamento: Record<string, number>;
  aVincular: number;
  aMover: number;
  semMudanca: number;
  /** true = dá para aplicar (nenhum problema bloqueia e há pelo menos 1 mudança). */
  podeAplicar: boolean;
}

/**
 * Monta o plano da importação sem gravar nada: casa cada linha com a UC e o agrupamento,
 * separa o que vincula do que move e lista os problemas. A tela mostra isso antes de aplicar.
 */
export function planejarImportacao(
  linhas: LinhaImportacao[],
  ucs: (UcParaPendencia & { id: number })[],
  /**
   * Cada agrupamento com TODOS os seus códigos (a SAP da Prefeitura tem dois). O campo antigo
   * `cod_agrupamento_concessionaria` continua aceito para não quebrar quem ainda o passa.
   */
  agrupamentos: { id: number; codigos?: string[] | null; cod_agrupamento_concessionaria?: string | null; ativo: boolean }[],
  vinculoAtual: Map<number, number>,
): PlanoImportacao {
  const porNumero = new Map<string, UcParaPendencia & { id: number }>();
  for (const u of ucs) {
    const n = normalizarNumeroUc(u.numero_uc);
    if (n && !porNumero.has(n)) porNumero.set(n, u);
  }
  const porCodigo = new Map<string, { id: number; ativo: boolean }>();
  for (const a of agrupamentos) {
    const codigos = normalizarListaCodigos([...(a.codigos ?? []), a.cod_agrupamento_concessionaria ?? '']);
    for (const c of codigos) porCodigo.set(c.toUpperCase(), { id: a.id, ativo: a.ativo });
  }

  const itens: ItemImportacao[] = [];
  const problemas: ProblemaImportacao[] = [];
  const codigoPorUc = new Map<string, string>();
  const jaVista = new Set<string>();

  for (const l of linhas) {
    const uc = normalizarNumeroUc(l.numero_uc);
    const cod = normalizarCodigoAgrupamento(l.cod_agrupamento);
    if (!uc || !cod) {
      problemas.push({ linha: l.linha, numero_uc: String(l.numero_uc ?? ''), motivo: 'linha_incompleta' });
      continue;
    }
    const codMaiusc = cod.toUpperCase();
    // Dois códigos do MESMO agrupamento (Administração e Esporte na SAP) são o mesmo destino:
    // a chave de comparação é o agrupamento quando ele existe, e o código só quando não existe.
    const destino = porCodigo.has(codMaiusc) ? `g:${porCodigo.get(codMaiusc)!.id}` : `c:${codMaiusc}`;
    const anterior = codigoPorUc.get(uc);
    if (anterior !== undefined) {
      problemas.push({
        linha: l.linha,
        numero_uc: uc,
        motivo: anterior === destino ? 'uc_repetida_na_planilha' : 'uc_repetida_em_dois_agrupamentos',
      });
      continue;
    }
    codigoPorUc.set(uc, destino);

    const cadastro = porNumero.get(uc);
    if (!cadastro) {
      problemas.push({ linha: l.linha, numero_uc: uc, motivo: 'uc_fora_do_cadastro' });
      continue;
    }
    if (cadastro.empresa_parceira_id != null) {
      problemas.push({ linha: l.linha, numero_uc: uc, motivo: 'uc_de_empresa_parceira' });
      continue;
    }
    const grupo = porCodigo.get(codMaiusc);
    if (!grupo) {
      problemas.push({ linha: l.linha, numero_uc: uc, motivo: 'agrupamento_desconhecido' });
      continue;
    }
    if (!grupo.ativo) {
      problemas.push({ linha: l.linha, numero_uc: uc, motivo: 'agrupamento_inativo' });
      continue;
    }
    if (jaVista.has(uc)) continue;
    jaVista.add(uc);

    const atual = vinculoAtual.get(cadastro.id);
    itens.push({
      linha: l.linha,
      numero_uc: uc,
      cod_agrupamento: cod,
      unidade_consumidora_id: cadastro.id,
      agrupamento_id: grupo.id,
      acao: atual === grupo.id ? 'sem_mudanca' : atual === undefined ? 'vincular' : 'mover',
      ...(atual !== undefined ? { agrupamento_atual_id: atual } : {}),
    });
  }

  const porAgrupamento: Record<string, number> = {};
  for (const i of itens) {
    if (i.acao === 'sem_mudanca') continue;
    porAgrupamento[i.cod_agrupamento] = (porAgrupamento[i.cod_agrupamento] ?? 0) + 1;
  }
  const aVincular = itens.filter(i => i.acao === 'vincular').length;
  const aMover = itens.filter(i => i.acao === 'mover').length;
  const bloqueia = problemas.some(p => BLOQUEIA_IMPORTACAO.includes(p.motivo));

  return {
    itens,
    problemas,
    porAgrupamento,
    aVincular,
    aMover,
    semMudanca: itens.filter(i => i.acao === 'sem_mudanca').length,
    podeAplicar: !bloqueia && aVincular + aMover > 0,
  };
}

/** Agrupa o plano em uma chamada de RPC por agrupamento, só com o que muda algo. */
export function lotesDaImportacao(plano: PlanoImportacao): { agrupamento_id: number; uc_ids: number[] }[] {
  const porGrupo = new Map<number, number[]>();
  for (const i of plano.itens) {
    if (i.acao === 'sem_mudanca') continue;
    const atual = porGrupo.get(i.agrupamento_id) ?? [];
    atual.push(i.unidade_consumidora_id);
    porGrupo.set(i.agrupamento_id, atual);
  }
  return [...porGrupo].map(([agrupamento_id, uc_ids]) => ({ agrupamento_id, uc_ids }));
}

/**
 * Lê a resposta das RPCs vincular/desvincular. Só `ok: true` confirma gravação — resposta vazia
 * ou sem `ok` é tratada como falha, nunca como sucesso.
 */
export function interpretarResultadoVinculo(resp: unknown): VincularUcsAgrupamentoResultado {
  const r = (resp ?? {}) as Record<string, unknown>;
  if (r.ok !== true) throw new Error('O banco não confirmou o vínculo. Nada foi considerado gravado.');
  // Aceita string numérica: contagem serializada como texto não pode virar 0 e dizer "nada mudou".
  const n = (v: unknown) => {
    const num = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
    return typeof num === 'number' && Number.isFinite(num) ? num : 0;
  };
  return {
    ok: true,
    vinculadas: n(r.vinculadas),
    movidas: n(r.movidas),
    ja_estavam: n(r.ja_estavam),
    desvinculadas: n(r.desvinculadas),
    nao_estavam: n(r.nao_estavam),
  };
}

/** Frase de feedback que não diz "sucesso" quando nada mudou. */
export function resumoVinculo(r: VincularUcsAgrupamentoResultado): string {
  const partes: string[] = [];
  if (r.vinculadas) partes.push(`${r.vinculadas} vinculada${r.vinculadas > 1 ? 's' : ''}`);
  if (r.movidas) partes.push(`${r.movidas} movida${r.movidas > 1 ? 's' : ''}`);
  if (r.desvinculadas) partes.push(`${r.desvinculadas} desvinculada${r.desvinculadas > 1 ? 's' : ''}`);
  if (partes.length === 0) return 'Nenhuma alteração: as UCs já estavam como pedido.';
  const sobra = r.ja_estavam + r.nao_estavam;
  return partes.join(', ') + (sobra ? ` (${sobra} sem alteração)` : '') + '.';
}
